/**
 * Hint generators for each achievement
 * Each function analyzes the current state and provides minimal, encouraging hints
 * that give students one clear next step without revealing the complete solution
 */

import type { HintResult, HintGenerator } from './hintTypes';
import type { BlockLine } from '../../types/blocks';
import {
  hasExactBlockCounts,
  hasBlockCountsInAnyOrder,
  findLineByLabel,
  getCorrectLineIndex,
  describeValuePosition,
  areRatiosEquivalent,
} from './hintHelpers';

/**
 * Hint for 'double' achievement: Create a 1:2 ratio
 */
export const doubleHint: HintGenerator = (lines, arrangement, multiplier) => {
  void arrangement;
  void multiplier;
  const expected = [1, 2];
  const actualCounts = lines.map(l => l.blocks);

  // Check if they have the right number of lines
  if (lines.length > 2) {
    return {
      message: 'A ratio of two parts needs exactly two values. Try clicking the 🗑️ button to remove the extra ones.',
    };
  }

  if (lines.length < 2) {
    return {
      message: 'You need two values to make a ratio. Click the "Add value" button.',
    };
  }

  // NEW: Check if they have an equivalent ratio (e.g., 2:4, 3:6, etc.)
  if (lines.length === 2 && areRatiosEquivalent(actualCounts, expected) && !hasExactBlockCounts(lines, expected)) {
    return {
      encouragement: "You put in a ratio that's mathematically equivalent to the target. Great job!",
      message: "For this problem, we're looking for the form that uses the numbers directly from the description.",
    };
  }

  // Check if they have the correct values but in wrong order
  if (hasBlockCountsInAnyOrder(lines, expected) && !hasExactBlockCounts(lines, expected)) {
    return {
      encouragement: 'You have 1 and 2 blocks!',
      message: 'The order matters in a ratio. Try adjusting which value has which count using the + and - buttons.',
    };
  }

  // Check if exactly one value is correct
  const correctIndex = getCorrectLineIndex(lines, expected);
  if (correctIndex !== -1) {
    const incorrectIndex = correctIndex === 0 ? 1 : 0;
    return {
      encouragement: `Good! Your ${describeValuePosition(correctIndex)} value is correct.`,
      message: `Now use the + or - buttons to adjust your ${describeValuePosition(incorrectIndex)} value.`,
    };
  }

  // General guidance
  return {
    message: 'Try creating a ratio of 1 to 2. Use the + and - buttons to adjust the block counts.',
  };
};

/**
 * Hint for '3-to-8' achievement: Create a 3:8 ratio
 */
export const threeToEightHint: HintGenerator = (lines, arrangement, multiplier) => {
  void arrangement;
  void multiplier;
  const expected = [3, 8];
  const actualCounts = lines.map(l => l.blocks);

  if (lines.length > 2) {
    return {
      message: 'This ratio needs exactly two values. Click the 🗑️ button to remove the extras.',
    };
  }

  if (lines.length < 2) {
    return {
      message: 'You need two values for this ratio. Click the "Add value" button.',
    };
  }

  // NEW: Check if they have an equivalent ratio (e.g., 6:16, 9:24, etc.)
  if (lines.length === 2 && areRatiosEquivalent(actualCounts, expected) && !hasExactBlockCounts(lines, expected)) {
    return {
      encouragement: "You put in a ratio that's mathematically equivalent to the target. Great job!",
      message: "For this problem, we're looking for the form that uses the numbers directly from the description.",
    };
  }

  // Check if they have the correct values but in wrong order
  if (hasBlockCountsInAnyOrder(lines, expected) && !hasExactBlockCounts(lines, expected)) {
    return {
      encouragement: 'You have 3 and 8 blocks!',
      message: 'The order matters. Use the + and - buttons to swap which value has which count.',
    };
  }

  // Check if exactly one value is correct
  const correctIndex = getCorrectLineIndex(lines, expected);
  if (correctIndex !== -1) {
    const incorrectIndex = correctIndex === 0 ? 1 : 0;
    return {
      encouragement: `Good! Your ${describeValuePosition(correctIndex)} value is correct.`,
      message: `Now use the + or - buttons to adjust your ${describeValuePosition(incorrectIndex)} value.`,
    };
  }

  return {
    message: 'Try creating a ratio of 3 to 8. Use the + and - buttons to adjust the block counts.',
  };
};

/**
 * Hint for '8-to-3' achievement: Create an 8:3 ratio
 */
export const eightToThreeHint: HintGenerator = (lines, arrangement, multiplier) => {
  void arrangement;
  void multiplier;
  const expected = [8, 3];
  const actualCounts = lines.map(l => l.blocks);

  if (lines.length > 2) {
    return {
      message: 'This ratio needs exactly two values. Click the 🗑️ button to remove the extras.',
    };
  }

  if (lines.length < 2) {
    return {
      message: 'You need two values for this ratio. Click the "Add value" button.',
    };
  }

  // Check if they have 3:8 instead (the reverse)
  if (hasExactBlockCounts(lines, [3, 8])) {
    return {
      encouragement: 'You have a 3:8 ratio.',
      message: 'This challenge asks for 8:3 instead. Use the + and - buttons to swap the counts.',
    };
  }

  // NEW: Check if they have an equivalent ratio (e.g., 16:6, 24:9, etc.)
  if (lines.length === 2 && areRatiosEquivalent(actualCounts, expected) && !hasExactBlockCounts(lines, expected)) {
    return {
      encouragement: "You put in a ratio that's mathematically equivalent to the target. Great job!",
      message: "For this problem, we're looking for the form that uses the numbers directly from the description.",
    };
  }

  // Check if they have the correct values but in wrong order
  if (hasBlockCountsInAnyOrder(lines, expected) && !hasExactBlockCounts(lines, expected)) {
    return {
      encouragement: 'You have 8 and 3 blocks!',
      message: 'Use the + and - buttons to swap which value has which count.',
    };
  }

  // Check if exactly one value is correct
  const correctIndex = getCorrectLineIndex(lines, expected);
  if (correctIndex !== -1) {
    const incorrectIndex = correctIndex === 0 ? 1 : 0;
    return {
      encouragement: `Your ${describeValuePosition(correctIndex)} value is correct.`,
      message: `Use the + or - buttons to adjust your ${describeValuePosition(incorrectIndex)} value.`,
    };
  }

  return {
    message: 'Try creating a ratio of 8 to 3. Use the + and - buttons to adjust the block counts.',
  };
};

/**
 * Hint for 'bikes' achievement: Create bikes:tires 1:2 ratio with labels
 * Priority: Check labels first, then counts
 */
export const bikesHint: HintGenerator = (lines, arrangement, multiplier) => {
  void arrangement;
  void multiplier;
  if (lines.length > 2) {
    return {
      message: 'This ratio needs exactly two values. Click the 🗑️ button to remove the extras.',
    };
  }

  if (lines.length < 2) {
    return {
      message: 'You need two values for this ratio. Click the "Add value" button.',
    };
  }

  // PRIORITY 1: Check labels first (accept singular or plural)
  const bikesLine = findLineByLabel(lines, 'bike', 'bikes');
  const tiresLine = findLineByLabel(lines, 'tire', 'tires');
  const hasBothLabels = bikesLine && tiresLine;

  // Missing both labels
  if (!bikesLine && !tiresLine) {
    return {
      message: 'Start by clicking on a label to rename it to "bike" or "tires".',
    };
  }

  // Have one label but not the other
  if (!hasBothLabels) {
    const hasLabel = bikesLine ? bikesLine.label ?? 'bikes' : tiresLine!.label ?? 'tires';
    const missingLabel = bikesLine ? 'tires' : 'bikes';
    return {
      encouragement: `Good! You labeled one as "${hasLabel}".`,
      message: `Now click on the other label to rename it to "${missingLabel}".`,
    };
  }

  // PRIORITY 2: Have both labels, check counts
  if (bikesLine.blocks === 1 && tiresLine.blocks === 2) {
    return {
      message: 'You have bikes and tires labeled correctly with the right counts!',
    };
  }

  // Have labels but wrong counts
  return {
    encouragement: `Perfect! You have "${bikesLine.label}" and "${tiresLine.label}" labeled.`,
    message: 'Now use the + and - buttons to adjust the counts. How many tires does each bike have?',
  };
};

/**
 * Hint for 'multi-bike' achievement: Bikes:tires with multiples
 * Priority: Check labels and counts, then multiples
 */
export const multiBikeHint: HintGenerator = (lines, arrangement, multiplier) => {
  void arrangement;
  // PRIORITY 1: Check labels first (accept singular or plural)
  const bikesLine = findLineByLabel(lines, 'bike', 'bikes');
  const tiresLine = findLineByLabel(lines, 'tire', 'tires');
  const hasBothLabels = bikesLine && tiresLine;

  if (!hasBothLabels) {
    return {
      message: 'First, create a bikes:tires ratio with those labels. Click on the labels to rename them.',
    };
  }

  // PRIORITY 2: Check counts
  if (bikesLine.blocks !== 1 || tiresLine.blocks !== 2) {
    return {
      encouragement: `You have "${bikesLine.label}" and "${tiresLine.label}" labeled!`,
      message: `Use the + and - buttons to make sure "${bikesLine.label}" has 1 block and "${tiresLine.label}" has 2 blocks.`,
    };
  }

  // PRIORITY 3: Check multiples
  if (multiplier < 2) {
    return {
      encouragement: `Perfect! You have ${bikesLine.label}:${tiresLine.label} set up as 1:2.`,
      message: 'Now click the →× button (to the right of the blocks) to create multiple instances.',
    };
  }

  // They should have completed
  return {
    message: `Make sure your ${bikesLine.label}:${tiresLine.label} ratio is set up as 1:2 with multiples.`,
  };
};

/**
 * Hint for 'sequenced' achievement: Create 1:2:3 in sequence mode
 */
export const sequencedHint: HintGenerator = (lines, arrangement, multiplier) => {
  void multiplier;
  const expected = [1, 2, 3];

  // Check if they have the right number of lines
  if (lines.length < 3) {
    return {
      message: `This is a three-part ratio. Click the "Add value" button to add ${3 - lines.length} more value${
        3 - lines.length === 1 ? '' : 's'
      }.`,
    };
  }

  if (lines.length > 3) {
    return {
      message: 'You only need 3 values for this ratio. Click the 🗑️ button to remove the extras.',
    };
  }

  // Check if they have the right counts
  const hasCorrectCounts = hasExactBlockCounts(lines, expected);

  // Check if they're in parallel mode with correct values
  if (hasCorrectCounts && arrangement === 'parallel') {
    return {
      encouragement: 'Excellent! You have a 1:2:3 ratio.',
      message: 'Now click the ☰ button to switch to the sequenced arrangement.',
    };
  }

  // They're in sequence mode but don't have correct counts
  if (arrangement === 'sequence' && !hasCorrectCounts) {
    // Check if they have the right counts in any order
    if (hasBlockCountsInAnyOrder(lines, expected)) {
      return {
        encouragement: "Great! You have 1, 2, and 3 blocks and you're using the sequenced arrangement.",
        message: 'Use the + and - buttons to adjust the order.',
      };
    }

    // Check how many are correct
    let correctCount = 0;
    for (let i = 0; i < 3; i++) {
      if (lines[i].blocks === expected[i]) {
        correctCount++;
      }
    }

    if (correctCount > 0) {
      return {
        encouragement: `Good! You have ${correctCount} value${
          correctCount === 1 ? '' : 's'
        } correct and are using the sequenced arrangement.`,
        message: 'Use the + and - buttons to adjust the other values.',
      };
    }

    return {
      message: "You're in the sequenced arrangement! Use the + and - buttons to create a 1:2:3 ratio.",
    };
  }

  // They're in parallel mode and don't have correct counts
  if (arrangement === 'parallel') {
    return {
      message: 'Use the + and - buttons to create a 1:2:3 ratio.',
    };
  }

  // General guidance
  return {
    message: 'Create a three-part ratio: 1:2:3, then click the ☰ button to switch arrangements.',
  };
};

/**
 * Hint for 'equivalent-ratios' achievement: 5:9 with 5+ multiples
 */
export const equivalentRatiosHint: HintGenerator = (lines, arrangement, multiplier) => {
  void arrangement;
  const expected = [5, 9];

  if (lines.length > 2) {
    return {
      message: 'This ratio needs exactly two values. Click the 🗑️ button to remove the extras.',
    };
  }

  if (lines.length < 2) {
    return {
      message: 'You need two values for this ratio. Click the "Add value" button.',
    };
  }

  const hasCorrectCounts = hasExactBlockCounts(lines, expected);

  // They have the right counts but not enough multiples
  if (hasCorrectCounts && multiplier < 5) {
    if (multiplier === 1) {
      return {
        encouragement: 'Great! You have a 5:9 ratio.',
        message: 'Now click the →× button to create multiple instances.',
      };
    }
    const remaining = 5 - multiplier;
    return {
      encouragement: `You have a 5:9 ratio with ${multiplier} multiple${multiplier === 1 ? '' : 's'}.`,
      message: `Click the →× button ${remaining} more time${remaining === 1 ? '' : 's'}.`,
    };
  }

  // They have multiples but wrong counts
  if (!hasCorrectCounts && multiplier >= 5) {
    return {
      encouragement: 'Excellent! You have multiple instances.',
      message: 'Use the + and - buttons to adjust your block counts to 5:9.',
    };
  }

  // Check if they have the correct values but in wrong order
  if (hasBlockCountsInAnyOrder(lines, expected) && !hasExactBlockCounts(lines, expected)) {
    return {
      encouragement: 'You have 5 and 9 blocks!',
      message: 'Use the + and - buttons to swap the order.',
    };
  }

  // Check if exactly one value is correct
  const correctIndex = getCorrectLineIndex(lines, expected);
  if (correctIndex !== -1) {
    const incorrectIndex = correctIndex === 0 ? 1 : 0;
    return {
      encouragement: `Your ${describeValuePosition(correctIndex)} value is correct.`,
      message: `Use the + or - buttons to adjust your ${describeValuePosition(incorrectIndex)} value.`,
    };
  }

  return {
    message: 'Create a 5:9 ratio using the + and - buttons, then click the →× button to make multiple copies.',
  };
};

/**
 * Hint for 'spiders' achievement: spiders:legs 1:8 with labels
 * Priority: Check labels first, then counts
 */
export const spidersHint: HintGenerator = (lines, arrangement, multiplier) => {
  void arrangement;
  void multiplier;
  if (lines.length > 2) {
    return {
      message: 'This ratio needs exactly two values. Click the 🗑️ button to remove the extras.',
    };
  }

  if (lines.length < 2) {
    return {
      message: 'You need two values for this ratio. Click the "Add value" button.',
    };
  }

  // PRIORITY 1: Check labels first (accept singular or plural)
  const spidersLine = findLineByLabel(lines, 'spider', 'spiders');
  const legsLine = findLineByLabel(lines, 'leg', 'legs');
  const hasBothLabels = spidersLine && legsLine;

  // Missing both labels
  if (!spidersLine && !legsLine) {
    return {
      message: 'Start by clicking on a label to rename it to "spider" or "legs".',
    };
  }

  // Have one label but not the other
  if (!hasBothLabels) {
    const hasLabel = spidersLine ? spidersLine.label ?? 'spiders' : legsLine!.label ?? 'legs';
    const missingLabel = spidersLine ? 'legs' : 'spiders';
    return {
      encouragement: `Good! You labeled one as "${hasLabel}".`,
      message: `Now click on the other label to rename it to "${missingLabel}".`,
    };
  }

  // PRIORITY 2: Have both labels, check counts
  if (spidersLine.blocks === 1 && legsLine.blocks === 8) {
    return {
      message: 'You have spiders and legs labeled correctly with the right counts!',
    };
  }

  // Have labels but wrong counts
  return {
    encouragement: `Perfect! You have "${spidersLine.label}" and "${legsLine.label}" labeled.`,
    message: 'Use the + and - buttons to adjust the counts. How many legs does each spider have?',
  };
};

/**
 * Hint for 'part-to-whole' achievement: red:white 7:6 for American flag stripes
 * Priority: Check color labels first, then counts
 */
export const partToWholeHint: HintGenerator = (lines, arrangement, multiplier) => {
  void arrangement;
  void multiplier;
  if (lines.length > 2) {
    return {
      message: 'This ratio needs exactly two values. Click the 🗑️ button to remove the extras.',
    };
  }

  if (lines.length < 2) {
    return {
      message: 'You need two values for this ratio. Click the "Add value" button.',
    };
  }

  // PRIORITY 1: Check color labels first
  const redLine = lines.find(l => l.label?.toLowerCase().includes('red'));
  const whiteLine = lines.find(l => l.label?.toLowerCase().includes('white'));
  const hasBothColorLabels = redLine && whiteLine;

  // Missing both color labels
  if (!redLine && !whiteLine) {
    return {
      message: 'Start by clicking on a label to add a color name (like "red" or "white").',
    };
  }

  // Have one color label but not the other
  if (!hasBothColorLabels) {
    const hasColor = redLine ? 'red' : 'white';
    const missingColor = redLine ? 'white' : 'red';
    return {
      encouragement: `Good! You have "${hasColor}" in one label.`,
      message: `Now click on the other label to add "${missingColor}" to it.`,
    };
  }

  // PRIORITY 2: Have both color labels, check counts
  // Check if they have the right counts on the right labels
  if (redLine.blocks === 7 && whiteLine.blocks === 6) {
    // Should have completed - shouldn't reach here
    return {
      message: 'You have the color labels and correct counts for the American flag!',
    };
  }

  // Check if they have the counts reversed
  const redCount = redLine.blocks;
  const whiteCount = whiteLine.blocks;

  if (redCount === 6 && whiteCount === 7) {
    return {
      encouragement: 'Perfect! You have color labels and the right counts.',
      message: 'Use the + and - buttons to swap the counts. Which color has more stripes on the American flag?',
    };
  }

  // Have labels but wrong counts
  return {
    encouragement: 'Excellent! You have color labels.',
    message: 'Use the + and - buttons to adjust the counts. Think about the American flag stripes.',
  };
};

/**
 * Map of achievement IDs to their hint generators
 */
export const ACHIEVEMENT_HINTS: Record<string, HintGenerator> = {
  double: doubleHint,
  '3-to-8': threeToEightHint,
  '8-to-3': eightToThreeHint,
  bikes: bikesHint,
  'multi-bike': multiBikeHint,
  sequenced: sequencedHint,
  'equivalent-ratios': equivalentRatiosHint,
  spiders: spidersHint,
  'part-to-whole': partToWholeHint,
};

/**
 * Get a contextual hint for an achievement based on current state
 */
export function getAchievementHint(
  achievementId: string,
  lines: BlockLine[],
  arrangement: 'parallel' | 'sequence',
  cloneMultiplier: number,
): HintResult {
  const hintGenerator = ACHIEVEMENT_HINTS[achievementId];

  if (!hintGenerator) {
    return {
      message: 'No hint available for this achievement yet. Keep experimenting!',
    };
  }

  return hintGenerator(lines, arrangement, cloneMultiplier);
}
